import { useEffect, useState, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Share2, Grid, Play, Bookmark, Check, BadgeCheck, Phone, Instagram, Facebook, Eye, EyeOff, Plus, RefreshCw, MessageSquare } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { useSettings, buildWhatsAppLink, onWhatsAppClick } from "@/contexts/SettingsContext";
import { useAdmin } from "@/contexts/AdminContext";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { TikTokIcon, PinterestIcon } from "@/components/SocialIcons";
import SiteVisitsCounter from "@/components/SiteVisitsCounter";
import GalleryTile, { EmptyTile } from "@/components/GalleryTile";
import CategoryToggle from "@/components/CategoryToggle";
import { EditableText, EditableImage } from "@/components/InlineEdit";
import { readCache, writeCache, isMeaningfulResponse, invalidateCache } from "@/lib/persistCache";
import { toast } from "sonner";

const DEFAULT_PROFILE_IMG =
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=600&q=80";

export default function Home() {
  const { settings } = useSettings();
  const { isAdmin, editing, toggleEditing } = useAdmin();
  const navigate = useNavigate();
  // Cache strategy:
  //  • VISITORS (editing=false) → hydrate instantly from localStorage cache
  //    so they never see a blank gallery during backend pod restarts.
  //  • ADMINS (editing=true) → ALWAYS bypass the cache. Admin must see the
  //    real DB state at all times so the admin grid never lags behind what
  //    a fresh visitor sees on another device. The cache is for visitor
  //    perceived-speed, not for editor truth.
  const cachedFirstPage = editing
    ? null
    : readCache(`projects:public:tab=all:cat=all`);
  const [projects, setProjects] = useState(cachedFirstPage?.items || []);
  const [tab, setTab] = useState("all");
  const [category, setCategory] = useState("all");
  const [loading, setLoading] = useState(!cachedFirstPage);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(cachedFirstPage?.total || 0);
  const [skip, setSkip] = useState(0);
  const [copied, setCopied] = useState(false);
  const sentinelRef = useRef(null);
  const PAGE = settings?.page_size || 24;

  // Reset list when tab or category changes
  useEffect(() => { setProjects([]); setSkip(0); setHasMore(true); }, [tab, category, editing]);

  const load = useCallback(async (offset = 0) => {
    try {
      if (offset === 0) setLoading(true); else setLoadingMore(true);
      if (tab === "saved") {
        const { data } = await api.get("/me/saved");
        setProjects(data.items); setTotal(data.items.length); setHasMore(false);
        return;
      }
      const params = { limit: PAGE, skip: offset };
      if (tab === "videos") params.is_video = true;
      if (category !== "all") params.category = category;
      // Admins see drafts + archived too
      if (editing) {
        params.include_unpublished = true;
        params.include_archived = true;
      }
      const { data } = await api.get("/projects", { params });
      const meaningful = isMeaningfulResponse(data) && Array.isArray(data.items);
      // Persist the FIRST page of the default VISITOR view only — admin
      // writes never feed the visitor cache so admin previews never poison
      // the public snapshot.
      if (meaningful && offset === 0 && tab === "all" && category === "all" && !editing) {
        writeCache("projects:public:tab=all:cat=all", { items: data.items, total: data.total });
      }
      // Only overwrite UI state with the live response if it's meaningful;
      // otherwise keep whatever we already have on screen (cached snapshot).
      if (meaningful || offset > 0) {
        setProjects((prev) => offset === 0 ? data.items : [...prev, ...data.items]);
        if (offset === 0) setTotal(data.total);
        setHasMore(offset + data.items.length < data.total);
        setSkip(offset + data.items.length);
      }
    } catch (e) {
      // Network error / backend cold-start: keep existing UI (cache).
      // eslint-disable-next-line no-console
      console.error("Failed to load projects (using cache fallback):", e?.message || e);
    }
    finally { setLoading(false); setLoadingMore(false); }
  }, [tab, PAGE, category, editing]);

  useEffect(() => { load(0); }, [load]);

  useEffect(() => {
    if (!hasMore || loading || loadingMore) return;
    const el = sentinelRef.current; if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) load(skip);
    }, { rootMargin: "800px 0px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loading, loadingMore, skip, load]);

  const shareProfile = async () => {
    const url = window.location.origin;
    if (navigator.share) {
      try { await navigator.share({ title: settings?.brand_name, text: settings?.tagline, url }); return; }
      catch { /* cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true); setTimeout(() => setCopied(false), 1800);
      toast.success("Profile link copied");
    } catch { toast.error("Unable to copy"); }
  };

  const TABS = [
    { key: "all", label: "Posts", icon: Grid },
    ...(settings?.show_reels_tab !== false ? [{ key: "videos", label: "Reels", icon: Play }] : []),
    ...(settings?.show_saved_tab !== false ? [{ key: "saved", label: "Saved", icon: Bookmark }] : []),
  ];

  const gridClass = "grid grid-cols-3 gap-0.5 md:gap-1";

  const profileImageRaw = settings?.profile_image || DEFAULT_PROFILE_IMG;
  const logoSrc = (() => {
    const u = settings?.logo_url;
    if (!u) return "/builddirect-logo.png";
    return u.startsWith("http") || u.startsWith("/") ? u : fileUrl(u);
  })();

  const socials = [
    settings?.contact_email && { key: "email", href: `mailto:${settings.contact_email}`, label: "Email", Icon: Mail, color: "" },
    settings?.mobile_number && { key: "mobile", href: `tel:${settings.mobile_number.replace(/[^\d+]/g, "")}`, label: "Call", Icon: Phone, color: "" },
    settings?.pinterest && { key: "pinterest", href: settings.pinterest, label: "Pinterest", Icon: PinterestIcon, color: "#E60023", external: true },
    settings?.tiktok && { key: "tiktok", href: settings.tiktok, label: "TikTok", Icon: TikTokIcon, color: "", external: true },
    settings?.instagram && { key: "instagram", href: settings.instagram, label: "Instagram", Icon: Instagram, color: "", external: true },
    settings?.facebook && { key: "facebook", href: settings.facebook, label: "Facebook", Icon: Facebook, color: "", external: true },
    settings?.whatsapp_number && { key: "whatsapp", href: buildWhatsAppLink(settings.whatsapp_number), label: "WhatsApp", Icon: WhatsAppIcon, color: "#25D366", external: true, isWA: true },
  ].filter(Boolean);

  const SLOT_TARGET = 60;

  // Hard-refresh handler — admin top-bar button. Nukes every cached
  // snapshot (projects, settings, categories) and re-fetches the home page
  // data immediately. Use this when the visitor view appears "more
  // up-to-date" than the admin view (the classic cross-device cache drift
  // pattern — admin's localStorage still holds yesterday's snapshot).
  const forceRefresh = async () => {
    try {
      invalidateCache("projects:");
      invalidateCache("categories");
      invalidateCache("settings");
    } catch { /* ignore */ }
    setProjects([]);
    setSkip(0);
    setHasMore(true);
    await load(0);
    toast.success("Refreshed from server");
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Admin top bar — visible only to admins */}
      {isAdmin && (
        <div className="sticky top-0 z-30 bg-[color:var(--bda-blue-glow)]/95 text-black flex items-center justify-between px-4 py-2 text-xs font-medium" data-testid="admin-top-bar">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-black animate-pulse" />
              {editing ? "ADMIN EDIT MODE" : "PREVIEW MODE"}
            </span>
            <Link to="/admin/dashboard" className="underline underline-offset-2">Dashboard</Link>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={forceRefresh}
              data-testid="admin-force-refresh"
              title="Reload from server (use if admin view doesn't match the live site)"
              className="inline-flex items-center gap-1.5 bg-black/85 text-white px-2.5 py-1 rounded-full"
              disabled={loading}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              onClick={() => navigate("/admin/projects/new")}
              data-testid="admin-new-project"
              className="inline-flex items-center gap-1.5 bg-black text-white px-2.5 py-1 rounded-full"
            >
              <Plus className="w-3.5 h-3.5" /> <span className="hidden sm:inline">New Post</span>
            </button>
            <button
              onClick={toggleEditing}
              data-testid="admin-toggle-editing"
              className="inline-flex items-center gap-1.5 bg-black/85 text-white px-2.5 py-1 rounded-full"
            >
              {editing ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">{editing ? "Hide controls" : "Edit mode"}</span>
            </button>
          </div>
        </div>
      )}

      {/* === PROFILE HEADER === */}
      <section className="border-b border-[color:var(--bda-border)] pt-7 md:pt-12" data-testid="profile-header">
        <div className="mx-auto max-w-[935px] px-5 md:px-10 pb-6 md:pb-9">
          {/* TOP ROW: profile image + logo */}
          <div className="flex flex-col sm:flex-row items-center sm:items-center justify-center gap-5 sm:gap-8 md:gap-12">
            <div className="flex-shrink-0">
              <EditableImage
                field="profile_image"
                value={profileImageRaw}
                alt={settings?.brand_name || "BuildDirect Africa"}
                className="w-28 h-28 sm:w-32 sm:h-32 md:w-40 md:h-40 rounded-full overflow-hidden bg-[color:var(--bda-surface-2)] border border-[color:var(--bda-border)] ring-2 ring-[color:var(--bda-blue-glow)]/15"
                imgClassName="w-full h-full object-cover"
                testId="profile-image"
              />
            </div>

            <div className="flex items-center justify-center gap-3 md:gap-4 min-w-0 max-w-full">
              <img
                src={logoSrc}
                alt={settings?.brand_name || "BuildDirect Africa"}
                data-testid="profile-brand-logo"
                className="h-10 sm:h-12 md:h-14 w-auto object-contain max-w-[55vw] sm:max-w-[300px] md:max-w-[340px] select-none"
                draggable={false}
              />
              <BadgeCheck
                className="w-6 h-6 md:w-7 md:h-7 text-[color:var(--bda-blue-glow)] drop-shadow-[0_0_8px_rgba(59,130,246,0.5)] flex-shrink-0"
                aria-label="Verified"
                data-testid="profile-verified-badge"
              />
            </div>
          </div>

          {/* === COMPACT INFO BLOCK ===
              v25h (2026-02-17): owner asked to remove the Stories row +
              tagline/bio to keep the header tight. Only the
              secondary-services line remains here. */}
          <div className="mt-6 md:mt-8 max-w-[640px] mx-auto text-center space-y-2.5">
            {(editing || settings?.secondary_services) && (
              <div className="text-[11px] md:text-[12px] text-[color:var(--bda-text-dim)] tracking-wide leading-relaxed px-2" data-testid="profile-secondary-services">
                <EditableText field="secondary_services" value={settings?.secondary_services} multiline placeholder="Secondary services" testId="profile-services" />
              </div>
            )}
            {/* Admin-only inline editors for the hidden fields so they're not
                lost / unreachable from the live UI when not in edit mode. */}
            {editing && (
              <>
                <div className="text-[11px] tracking-[0.2em] uppercase text-[color:var(--bda-text-dim)] mt-2" data-testid="profile-tagline-hidden">
                  <EditableText field="tagline" value={settings?.tagline} placeholder="Tagline (hidden on live site)" testId="profile-tagline" />
                </div>
                <div className="text-[11px] text-[color:var(--bda-text-dim)]" data-testid="profile-bio-hidden">
                  <EditableText field="bio" value={settings?.bio} multiline placeholder="Bio (hidden on live site)" testId="profile-bio" />
                </div>
              </>
            )}
          </div>

          {/* === UNIFIED CIRCULAR ICON ROW ===
              v25i (2026-02-17): owner asked to collapse the big WhatsApp
              and Contact pills + the larger Share circle into the same
              row of small circular icons. Consultation is now also a
              small icon here. Result: ONE compact row of communication
              icons — Email · Call · Instagram · WhatsApp · Consultation
              · Share — no big pills, no extra vertical space. */}
          <div className="mt-5 md:mt-6 flex flex-wrap items-center justify-center gap-2" data-testid="profile-socials">
            {socials.map(({ key, href, label, Icon, color, external, isWA }) => {
              const props = external ? { target: "_blank", rel: "noreferrer" } : {};
              return (
                <a
                  key={key} href={href} aria-label={label} title={label}
                  data-testid={`social-${key}`}
                  onClick={isWA ? onWhatsAppClick : undefined}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-[color:var(--bda-border)] text-[color:var(--bda-text-muted)] hover:text-[color:var(--bda-text)] hover:border-[color:var(--bda-blue-glow)]/40 hover:bg-[color:var(--bda-surface-2)] transition-all"
                  {...props}
                >
                  {isWA
                    ? <Icon className="w-4 h-4" color={color || "currentColor"} />
                    : <Icon className="w-4 h-4" {...(color ? { style: { color } } : {})} strokeWidth={1.5} />}
                </a>
              );
            })}
            <Link
              to="/consultation"
              data-testid="profile-consultation"
              aria-label="Consultation"
              title="Consultation"
              className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-[color:var(--bda-blue-glow)]/40 text-[color:var(--bda-blue-glow)] hover:bg-[color:var(--bda-blue-glow)]/15 transition-all"
            >
              <MessageSquare className="w-4 h-4" strokeWidth={1.5} />
            </Link>
            <button
              onClick={shareProfile}
              data-testid="profile-share"
              aria-label="Share profile"
              title="Share"
              className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-[color:var(--bda-border)] text-[color:var(--bda-text-muted)] hover:text-[color:var(--bda-text)] hover:border-[color:var(--bda-blue-glow)]/40 hover:bg-[color:var(--bda-surface-2)] transition-all"
            >
              {copied
                ? <Check className="w-4 h-4 text-emerald-400" strokeWidth={1.6} />
                : <Share2 className="w-4 h-4" strokeWidth={1.5} />}
            </button>
          </div>

          {/* === STATS ROW === */}
          <div className="mt-5 md:mt-6 grid grid-cols-3 gap-3 pt-5 border-t border-[color:var(--bda-border)]" data-testid="stats-row">
            <Stat label="Posts" value={total} testId="stat-posts" />
            {(editing || settings?.location_city || settings?.location_region) ? (
              <Stat
                label="Location"
                value={
                  editing
                    ? <EditableText field="location_city" value={settings?.location_city} placeholder="Location" />
                    : (settings?.location_city || "—")
                }
                sub={
                  editing
                    ? <EditableText field="location_region" value={settings?.location_region} placeholder="Region" />
                    : (settings?.location_region || null)
                }
                testId="stat-location"
              />
            ) : (
              <Stat label="Verified" value={<span className="text-[color:var(--bda-blue-glow)]">★</span>} testId="stat-verified" />
            )}
            <Stat label="Site Visits" value={<SiteVisitsCounter />} testId="stat-visits" highlight />
          </div>
        </div>
      </section>

      {/* === CATEGORY TOGGLE === */}
      <CategoryToggle />

      {/* TABS */}
      <div className={`sticky ${isAdmin ? "top-9" : "top-0"} bg-[color:var(--bda-bg)]/95 backdrop-blur z-20 border-b border-[color:var(--bda-border)]`} data-testid="gallery-tabs">
        <div className="mx-auto max-w-[935px] px-5 md:px-10">
          <div className="flex items-center justify-center gap-12 md:gap-16">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.key} onClick={() => setTab(t.key)}
                  data-testid={`gallery-tab-${t.key}`}
                  className={`inline-flex items-center gap-2.5 py-4 text-[10px] tracking-[0.4em] uppercase font-medium border-t transition-colors ${
                    tab === t.key
                      ? "text-[color:var(--bda-text)] border-[color:var(--bda-blue-glow)]"
                      : "text-[color:var(--bda-text-dim)] hover:text-[color:var(--bda-text-muted)] border-transparent"
                  }`}
                >
                  <Icon className="w-[14px] h-[14px]" strokeWidth={1.4} />
                  <span className="hidden sm:inline">{t.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* GALLERY */}
      <section className="mx-auto max-w-[935px] pb-24" data-testid="gallery-grid">
        {loading ? (
          <div className={gridClass} data-testid="gallery-loading">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="aspect-square bg-[color:var(--bda-surface-2)]/30 shimmer" />
            ))}
          </div>
        ) : projects.length === 0 && !editing ? (
          <div className="text-center py-16 px-6" data-testid="gallery-empty-state">
            <div className="font-heading text-base text-[color:var(--bda-text)] mb-1">
              {tab === "saved"
                ? "No saved posts yet"
                : category !== "all"
                ? "No posts in this category yet"
                : "Coming soon"}
            </div>
            <div className="text-xs text-[color:var(--bda-text-dim)] tracking-wide">
              {tab === "saved"
                ? "Tap the bookmark icon on any post to save it here."
                : "Check back soon — premium project shoots are on the way."}
            </div>
          </div>
        ) : (
          <>
            <div className={gridClass}>
              {projects.map((p) => <GalleryTile project={p} key={p.id} onChanged={() => load(0)} />)}

              {/* Empty placeholders — admin sees a clickable, upload-ready
                  tile for each empty slot up to SLOT_TARGET. Direct slot
                  upload: tap any empty tile → file picker → new post is
                  created with display_order = slotIndex, landing in this
                  exact box. No backend form, no auto-stacking. */}
              {tab === "all" && editing && projects.length < SLOT_TARGET &&
                Array.from({ length: SLOT_TARGET - projects.length }).map((_, i) => (
                  <EmptyTile
                    key={`empty-${i}`}
                    slotIndex={projects.length + i}
                    onUploaded={() => load(0)}
                  />
                ))}
            </div>
            {tab === "saved" && projects.length === 0 && (
              <div className="text-center py-24 text-[color:var(--bda-text-dim)] text-sm">Tap the bookmark icon on any post to save it here.</div>
            )}
            {hasMore && (
              <div ref={sentinelRef} className="h-20 flex items-center justify-center text-xs tracking-widest uppercase text-[color:var(--bda-text-dim)]">
                {loadingMore ? "Loading…" : ""}
              </div>
            )}
            {!hasMore && projects.length > 12 && (
              <div className="text-center py-8 text-xs tracking-[0.3em] uppercase text-[color:var(--bda-text-dim)]">
                — End of feed —
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value, sub, testId, highlight = false }) {
  return (
    <div className="text-center" data-testid={testId}>
      <div className={`font-heading text-base md:text-xl tracking-refined ${highlight ? "text-[color:var(--bda-blue-glow)] font-medium" : "text-[color:var(--bda-text)] font-medium"}`}>
        {value}
      </div>
      {sub && (
        <div className="text-[11px] md:text-[12px] text-[color:var(--bda-text-muted)] -mt-0.5">{sub}</div>
      )}
      <div className="text-[9px] md:text-[10px] text-[color:var(--bda-text-dim)] uppercase tracking-[0.3em] mt-1">{label}</div>
    </div>
  );
}
